import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthCard } from './_AuthLayout';

/**
 * Sign-up for affiliate-only partners. Creates a role='affiliate' account via
 * the dedicated endpoint — no plan, no dashboard. On success the user lands on
 * '/', which the App role-split renders as the affiliate portal.
 */
export function AffiliateSignUp() {
  const { affiliateSignUp, signOut, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await affiliateSignUp(name, email, password);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  }

  if (user) {
    return (
      <AuthCard
        title="You're already signed in"
        subtitle={`Logged in as ${user.email}. Sign out to create a new affiliate account, or continue.`}
      >
        <div className="space-y-3">
          <Button variant="primary" className="w-full" onClick={async () => { await signOut(); }}>
            Sign out &amp; create affiliate account
          </Button>
          <Button variant="secondary" className="w-full" onClick={() => navigate('/', { replace: true })}>
            Continue
          </Button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Become a TELOS affiliate"
      subtitle="Earn commission for every trader you refer. No subscription required."
    >
      <motion.form
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="space-y-4"
      >
        <Input label="Full name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Partner" required autoComplete="name" autoFocus />
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" />
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            required
            autoComplete="new-password"
            minLength={8}
            hint="Minimum 8 characters."
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            tabIndex={-1}
            className={cn(
              'absolute right-2 top-[26px] inline-flex items-center justify-center size-9 rounded-md',
              'text-fg-subtle hover:text-fg transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
            )}
          >
            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>

        {error && (
          <div role="alert" className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <Button type="submit" size="lg" loading={loading} className="w-full">
          Create affiliate account
        </Button>
      </motion.form>
      <div className="mt-6 text-center text-xs text-fg-muted">
        Already have an account? <Link to="/signin" className="text-accent font-semibold hover:underline">Sign in</Link>
      </div>
    </AuthCard>
  );
}
